import { useState } from "react";
import { useTranslation } from "react-i18next";

const RATES = [0.04, 1.03, 1.57, 1.98];
const RETURN_LABELS = ["3–5%", "103%", "157%", "198%"];
const TIER_AMOUNTS = [1500, 3000, 5000, 10000];

export default function CalculatorSection() {
  const { t } = useTranslation();
  const [selectedTier, setSelectedTier] = useState(1);
  const [amount, setAmount] = useState(3000);

  const activeTierIdx = selectedTier;
  const monthlyPension = Math.round(amount * RATES[activeTierIdx]);
  const annualDividend = Math.round(amount * RATES[activeTierIdx] * 12);
  const returnPct = RETURN_LABELS[activeTierIdx];

  const sliderPct = ((amount - 500) / (10000 - 500)) * 100;

  const tierLabelKeys = ["calc_tier1_label", "calc_tier2_label", "calc_tier3_label", "calc_tier4_label"];
  const tierSubKeys = ["calc_tier1_sub", "calc_tier2_sub", "calc_tier3_sub", "calc_tier4_sub"];
  const planNameKeys = ["calc_plan_starter", "calc_plan_optimal", "calc_plan_advanced", "calc_plan_premium"];

  const handleTierClick = (idx: number) => {
    setSelectedTier(idx);
    setAmount(TIER_AMOUNTS[idx]);
  };

  const handleSliderChange = (val: number) => {
    setAmount(val);
    if (val >= 10000) setSelectedTier(3);
    else if (val >= 5000) setSelectedTier(2);
    else if (val >= 3000) setSelectedTier(1);
    else setSelectedTier(0);
  };

  return (
    <section id="investment-slider" className="py-14 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-4 py-2 rounded-full mb-5">
            <i className="ri-calculator-line"></i>
            {t("calc_badge")}
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-3">{t("calc_title")}</h2>
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 text-sm font-semibold px-5 py-2.5 rounded-full mt-2">
            <i className="ri-flashlight-line text-amber-500"></i>
            {t("calc_promo_pre")} <strong className="mx-1">€3,000</strong> — {t("calc_promo_mid")} <strong className="mx-1">103%</strong> {t("calc_promo_post")}
          </div>
        </div>

        <div className="max-w-3xl mx-auto bg-stone-50 rounded-3xl p-6 md:p-12 border border-gray-100">
          <div className="mb-8">
            <div className="flex items-center justify-between mb-5">
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">{t("calc_amount_label")}</div>
              <div className="text-3xl font-bold text-gray-900">€{amount.toLocaleString()}</div>
            </div>

            <div className="relative h-10 flex items-center mb-2">
              <div className="absolute left-0 right-0 h-2 rounded-full bg-gray-200"></div>
              <div
                className="absolute left-0 h-2 rounded-l-full bg-emerald-500"
                style={{ width: `${sliderPct}%` }}
              ></div>
              <input
                min={500}
                max={10000}
                step={500}
                className="absolute left-0 right-0 w-full h-10 opacity-0 cursor-pointer"
                type="range"
                value={amount}
                onChange={(e) => handleSliderChange(Number(e.target.value))}
              />
            </div>

            <div className="flex justify-between text-xs text-gray-400 mb-4">
              <span>€500</span>
              <span className="font-bold text-amber-600">€3,000 🔓</span>
              <span>€5,000</span>
              <span>€10,000</span>
            </div>

            {/* Clickable tier cards */}
            <div className="grid grid-cols-2 gap-2">
              {tierLabelKeys.map((labelKey, idx) => {
                const isActive = idx === activeTierIdx;
                return (
                  <button
                    key={labelKey}
                    type="button"
                    onClick={() => handleTierClick(idx)}
                    className={`flex items-center gap-2 text-xs px-3 py-3 rounded-xl border-2 transition-all cursor-pointer text-left w-full ${
                      isActive
                        ? "border-amber-400 ring-2 ring-amber-300 scale-105 bg-white"
                        : "border-gray-200 opacity-60 hover:opacity-90 hover:border-gray-300 bg-white/60"
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive ? "bg-amber-400" : "bg-gray-300"}`}></div>
                    <div className="flex-1">
                      <div className="font-bold">{t(labelKey)}</div>
                      <div className="text-gray-500">{t(tierSubKeys[idx])}</div>
                    </div>
                    {isActive && <i className="ri-check-line font-bold text-amber-500"></i>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-gray-200 mb-8"></div>

          <div className="mb-8 flex items-center justify-between rounded-2xl px-6 py-5 border-2 bg-white border-amber-200">
            <div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">
                {t("calc_return_label")} &laquo;{t(planNameKeys[activeTierIdx])}&raquo;
              </div>
              <div className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full mt-1 bg-emerald-50 text-emerald-700">
                <i className="ri-arrow-up-line"></i>
                {returnPct}
              </div>
            </div>
            <div className="text-right">
              <div className="text-5xl font-bold text-gray-900">{returnPct}</div>
              <div className="text-xs text-gray-400 mt-1">{t("calc_per_year")}</div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">{t("calc_monthly_label")}</div>
              <div className="text-4xl font-bold text-gray-900 mb-1">€{monthlyPension.toLocaleString()}</div>
              <div className="text-xs text-gray-400">{t("calc_monthly_sub")}</div>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-gray-100">
              <div className="text-xs text-gray-400 font-semibold uppercase tracking-wider mb-2">{t("calc_dividend_label")}</div>
              <div className="text-4xl font-bold text-emerald-700 mb-1">€{annualDividend.toLocaleString()}</div>
              <div className="text-xs text-gray-400">{t("calc_dividend_sub")}</div>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-emerald-50 rounded-xl p-4 mb-6">
            <div className="w-5 h-5 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-line"></i>
            </div>
            <p className="text-sm text-emerald-800">
              <strong>€3,000</strong> — <strong>103%</strong> {t("calc_per_year")}. €10,000 — <strong>198%</strong>.
            </p>
          </div>

          <a
            href="#pension-options"
            className="block w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap text-base text-center"
          >
            {t("calc_cta")}
          </a>
        </div>
      </div>
    </section>
  );
}
